import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, ChevronLeft, ChevronRight, Loader2, AlertTriangle, Skull, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import inventory from "@/api/inventory";
import inventoryPdf, { pdfErrorMessage } from "@/api/inventoryPdf";
import PdfExportButton from "./PdfExportButton";

const PRESET_DAYS = [30, 60, 90, 180, 365, 730];

function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("en-US");
}
function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export default function InventoryDeadStockTable({
  filters,
  testId = "dead-stock-table",
  apiOverride,
  hideExport = false,
}) {
  const { t } = useTranslation();
  const api = apiOverride || inventory;
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [sortBy, setSortBy] = useState("dias_sin_movimiento");
  const [sortDir, setSortDir] = useState("desc");
  const [thresholdMode, setThresholdMode] = useState("90");
  const [customDays, setCustomDays] = useState(90);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const thresholdDays = thresholdMode === "custom" ? Math.max(1, Number(customDays) || 1) : Number(thresholdMode);

  useEffect(() => { setPage(1); }, [thresholdDays, filters?.preset, search]);
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getDeadStock({ filters, page, pageSize, sortBy, sortDir, search, thresholdDays })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (!cancelled) {
          toast.error(t("inventory.errors.deadStockLoad", { defaultValue: "No se pudo cargar dead stock" }));
          console.error(e);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, page, pageSize, sortBy, sortDir, search, thresholdDays, t]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / pageSize)), [data.total, pageSize]);

  const handleSort = (k) => {
    if (sortBy === k) setSortDir((d) => d === "desc" ? "asc" : "desc");
    else { setSortBy(k); setSortDir("desc"); }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await api.downloadDeadStockExport({ filters, sortBy, sortDir, search, thresholdDays });
      toast.success(t("inventory.export.success", { defaultValue: "Excel descargado" }));
    } catch (e) {
      toast.error(t("inventory.export.error", { defaultValue: "Fallo al exportar Excel" }));
      console.error(e);
    } finally { setExporting(false); }
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      await inventoryPdf.deadStock(filters, { sortBy, sortDir, search, threshold_days: thresholdDays });
      toast.success(t("inventory.exportPdf.success", { defaultValue: "PDF descargado" }));
    } catch (e) {
      toast.error(pdfErrorMessage(e, t));
      console.error(e);
    } finally { setExportingPdf(false); }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t("inventory.deadStock.thresholdLabel", { defaultValue: "Sin venta por:" })}
          </span>
          <Select value={thresholdMode} onValueChange={setThresholdMode}>
            <SelectTrigger className="h-8 w-[150px] text-sm" data-testid="dead-threshold-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESET_DAYS.map((d) => (
                <SelectItem key={d} value={String(d)}>{d} días</SelectItem>
              ))}
              <SelectItem value="custom">{t("inventory.deadStock.custom", { defaultValue: "Personalizado" })}</SelectItem>
            </SelectContent>
          </Select>
          {thresholdMode === "custom" && (
            <Input
              type="number"
              min={1}
              max={3650}
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              className="h-8 w-[100px] text-sm"
              data-testid="dead-custom-input"
            />
          )}
          <div className="relative w-full max-w-xs sm:w-[240px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder={t("inventory.searchPlaceholder", { defaultValue: "Buscar..." })}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-7 h-8 text-sm"
              data-testid="dead-search"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || loading} data-testid="dead-export-btn" className={hideExport ? "hidden" : ""}>
            {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
            {t("inventory.exportExcel", { defaultValue: "Exportar Excel" })}
          </Button>
          <PdfExportButton
            onClick={handleExportPdf}
            exporting={exportingPdf}
            disabled={loading}
            hidden={hideExport}
            testId="dead-pdf-btn"
          />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs cursor-pointer" onClick={() => handleSort("sku")}>SKU</TableHead>
                <TableHead className="text-xs">Descripción</TableHead>
                <TableHead className="text-xs">Categoría</TableHead>
                <TableHead className="text-xs cursor-pointer" onClick={() => handleSort("ultima_salida")}>Última salida</TableHead>
                <TableHead className="text-xs text-right cursor-pointer" onClick={() => handleSort("dias_sin_movimiento")}>
                  Días sin movimiento
                </TableHead>
                <TableHead className="text-xs text-right cursor-pointer" onClick={() => handleSort("stock_actual")}>Stock actual</TableHead>
                <TableHead className="text-xs text-right cursor-pointer" onClick={() => handleSort("costo_en_stock")}>Costo en stock</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && data.items.length === 0
                ? Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-3.5 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                : data.items.length === 0
                  ? <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">{t("inventory.empty", { defaultValue: "Sin resultados" })}</TableCell></TableRow>
                  : data.items.map((row) => (
                    <TableRow key={row.sku} data-testid={`dead-row-${row.sku}`}>
                      <TableCell className="text-xs font-mono">{row.sku}</TableCell>
                      <TableCell className="text-xs max-w-[240px] truncate">{row.description || "—"}</TableCell>
                      <TableCell className="text-xs">{row.category || "—"}</TableCell>
                      <TableCell className="text-xs tabular-nums">
                        {row.nunca_movido
                          ? <Badge variant="outline" className="text-[10px] text-rose-600 border-rose-600/40">
                              <Skull className="h-3 w-3 mr-1" />
                              {t("inventory.deadStock.neverMoved", { defaultValue: "Nunca movido" })}
                            </Badge>
                          : String(row.ultima_salida || "").slice(0, 10)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-xs">
                        {row.nunca_movido
                          ? <span className="text-rose-600">∞</span>
                          : (
                            <Badge
                              variant={row.dias_sin_movimiento > thresholdDays ? "destructive" : "secondary"}
                              className="text-[10px] tabular-nums"
                            >
                              {row.dias_sin_movimiento > thresholdDays && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {fmtNum(row.dias_sin_movimiento)} d
                            </Badge>
                          )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-xs">{fmtNum(row.stock_actual)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs">{fmtMoney(row.costo_en_stock)}</TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <div>
          {(data.total || 0).toLocaleString("en-US")} SKUs sin venta {">"}= {thresholdDays}d
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-7 w-7" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="tabular-nums px-2">{page} / {totalPages}</span>
          <Button variant="outline" size="icon" className="h-7 w-7" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
